import { clsx } from 'clsx';
import { FunctionComponent, useCallback } from 'react';
import { Divider } from '@mui/material';
import {
  EntityListMode,
  EntityListNavigationType,
  StandaloneModeActionsComponent
} from './interface';
import { EntityUIState } from '../request/interfaces/entity-ui-state';
import { SearchArea, SearchProps } from '../input/SearchArea/SearchArea';
import { Button } from '../button/Button';
import styles from './styles.module.scss';
import { PaginationControls } from '../pagination-controls/PaginationControls';
import { PaginationData } from '../pagination-controls/interfaces';
import { ListConductor } from '../list-conductor/list-conductor';
import { FilterList, ListNextPage, ListPrevPage } from '../list-conductor/list-actions';
import { OffsetPaginationData } from '../list-conductor/offset-pagination-strategy';
import { getDataIdGenerator } from '../shared/utils/dataId';
import { ListMetaData } from '../list-conductor/list-meta-data';
import { useAnalyticsTracker } from '../tracking/hooks/useAnalyticsTracker';
import { EntityListTrackers } from './entity-list-trackers';
import { DEFAULT_STRING_OPERATOR } from '../filter/constants';
import { SearchValueType } from '../input/SearchArea/interface';

export interface EntityListToolbarProps<S extends object, F, O> {
  className?: string;
  title?: string;
  showCount?: boolean;
  mode: EntityListMode;
  navigationType: EntityListNavigationType;
  compact?: boolean;
  compactToolbar?: boolean;
  compactControls?: boolean;
  searchKey?: string;
  onSearchAreaKeydown: SearchProps['keyDown'];
  onSearchAreaBlur: SearchProps['onBlur'];
  onSearchAreaFocus: SearchProps['onFocus'];
  defaultListMeta?: ListMetaData<OffsetPaginationData>;
  listConductor: ListConductor;
  uiState: EntityUIState;
  disableFilters?: boolean;
  showFilters?: boolean;
  showSearch?: boolean;
  onRefresh?: VoidFunction;
  searchPlaceholder?: string;
  StandaloneModeActions?: StandaloneModeActionsComponent<S>;
  standaloneModeActionsProps?: S;
  filtersComponentProps?: F;
  FiltersComponent?: FunctionComponent<F & { listConductor: ListConductor }>;
  SortComponent?: FunctionComponent<O & { listConductor: ListConductor; className?: string }>;
  sortComponentProps?: O;
  onPaginationChange?: (paginationData: OffsetPaginationData) => void;
  dataId: string;
  entity: string;
  showOperatorInSearch?: boolean;
}

export function EntityListToolbar<S extends object, F, O>({
  dataId,
  className,
  title,
  showCount,
  mode,
  navigationType,
  compact,
  compactToolbar,
  compactControls,
  searchKey,
  onSearchAreaKeydown,
  onSearchAreaBlur,
  onSearchAreaFocus,
  defaultListMeta,
  listConductor,
  uiState,
  showFilters,
  disableFilters,
  onRefresh,
  showSearch,
  searchPlaceholder,
  StandaloneModeActions,
  standaloneModeActionsProps,
  entity,
  FiltersComponent,
  filtersComponentProps,
  SortComponent,
  sortComponentProps,
  showOperatorInSearch = false
}: EntityListToolbarProps<S, F, O>) {
  const { eventTrack } = useAnalyticsTracker();

  const onSearchChange = useCallback((searchTerm: SearchValueType) => {
    eventTrack({
      action: EntityListTrackers.TRACKER_ENTITY_LIST_SEARCH,
      properties: {
        entity,
        search: searchTerm
      }
    });

    listConductor.dispatch(
      new FilterList([
        {
          filter: searchKey ?? '',
          value: typeof searchTerm === 'string' ? searchTerm : searchTerm.value,
          operator:
            typeof searchTerm === 'string' ? DEFAULT_STRING_OPERATOR.value : searchTerm.operator
        }
      ])
    );
  }, []);

  const onNextClick = () => {
    listConductor.dispatch(new ListNextPage());
  };

  const onPrevClick = () => {
    listConductor.dispatch(new ListPrevPage());
  };

  const listConductorMeta = listConductor.getListMeta();

  const filteredCount = (listConductorMeta.pagination as PaginationData)?.totalCount;

  const dataIdGenerator = getDataIdGenerator('entity-list-toolbar', dataId);

  const getDefaultSearchValue = (_searchKey: string) => {
    const filter = defaultListMeta?.filters.filter(value => value.filter === _searchKey) ?? [];

    if (filter.length) {
      return filter[0].value;
    }

    return '';
  };

  let paddingClass = '';
  if (compactToolbar) {
    paddingClass = 'py-2';
  } else if (mode === 'connected') {
    paddingClass = 'py-4';
  }

  const marginClass = mode === 'connected' ? 'px-5 border-bottom' : 'mb-5';

  const classNames = clsx('w-100 center-flex-row', marginClass, paddingClass, className);

  return (
    <div className={classNames} data-id={dataIdGenerator('')}>
      <div className='center-flex-row flex-grow-1'>
        {title ? (
          <h2
            data-id={dataIdGenerator('title')}
            className={clsx(
              'mr-2',
              mode === 'connected' ? 'text-body-2' : 'text-subheading-2',
              compact && 'text-body-2'
            )}
          >
            {title}

            {uiState === EntityUIState.IDLE && showCount ? (
              <>
                &nbsp;
                <span
                  className={clsx(mode === 'standalone' && 'text-subheading-1', 'text-secondary')}
                >
                  ({filteredCount})
                </span>
              </>
            ) : null}
          </h2>
        ) : null}

        {showSearch ? (
          <SearchArea
            dataId={dataIdGenerator('')}
            placeholder={searchPlaceholder}
            collapsible={false}
            autofocus
            keyDown={onSearchAreaKeydown}
            onBlur={onSearchAreaBlur}
            onFocus={onSearchAreaFocus}
            defaultSearch={searchKey ? (getDefaultSearchValue(searchKey) as string) : ''}
            disabled={!!disableFilters}
            onSearch={onSearchChange}
            showOperators={showOperatorInSearch}
            searchWrapperClassName={clsx(
              compactControls ? styles.searchAreaCompact : styles.searchArea
            )}
          />
        ) : null}

        {showFilters && FiltersComponent ? (
          <FiltersComponent listConductor={listConductor} {...(filtersComponentProps as F)} />
        ) : null}
      </div>

      <div className='center-flex-row'>
        {navigationType === EntityListNavigationType.PAGE_NUMBER &&
        listConductorMeta.pagination &&
        [
          EntityUIState.LOADING_MORE,
          EntityUIState.IDLE,
          EntityUIState.LOADING,
          EntityUIState.REFRESHING
        ].includes(uiState) ? (
          <>
            <PaginationControls
              paginationData={listConductorMeta.pagination as PaginationData}
              listState={uiState}
              nextPage={onNextClick}
              prevPage={onPrevClick}
              dataId={dataIdGenerator('pagination-controls')}
              trackingProps={{
                entity
              }}
            />

            {typeof onRefresh === 'function' ? (
              <Divider
                orientation='vertical'
                variant='middle'
                flexItem
                className='mx-4'
                data-id={dataIdGenerator('divider')}
              />
            ) : null}
          </>
        ) : null}

        {SortComponent ? (
          <SortComponent
            className='mr-5'
            listConductor={listConductor}
            {...(sortComponentProps as O)}
          />
        ) : null}

        {typeof onRefresh === 'function' ? (
          <Button
            dataId={dataIdGenerator('refresh')}
            variation='outline'
            size='md'
            icon='refresh'
            iconOnly={!!compactControls}
            onClick={() => {
              eventTrack({
                action: EntityListTrackers.TRACKER_ENTITY_LIST_REFRESH_CLICK,
                properties: {
                  entity
                }
              });

              onRefresh();
            }}
            disabled={
              !!disableFilters ||
              uiState === EntityUIState.LOADING ||
              uiState === EntityUIState.LOADING_MORE
            }
          >
            {compactControls ? '' : 'Refresh'}
          </Button>
        ) : null}

        {mode === 'standalone' && StandaloneModeActions ? (
          <StandaloneModeActions {...(standaloneModeActionsProps as S)} />
        ) : null}
      </div>
    </div>
  );
}
